import { Fragment } from 'react'
import classNames from 'classnames'

import {
  EMPTY_FUNCTION,
  EMPTY_LIST,
  EMPTY_OBJECT,
} from '../../../../constants/common'
import {
  DragDropProvider,
  SortableContextContainerProvider,
} from '../../../DragDrop'
import { useRequestProfileTabActivitiesContext } from '../RequestProfileTabActivities.hooks'
import {
  getPhaseDndId,
  getRequestProfileTabActivitiesInsertPhasePosition,
  hasRequestProfileTabActivitiesPhaseAbove,
  isActivitiesActivityType,
  isActivitiesPhaseType,
} from '../RequestProfileTabActivities.utils'
import { RequestProfileTabActivitiesActivityDragOverlay } from '../RequestProfileTabActivitiesActivityDragOverlay'
import { RequestProfileTabActivitiesInsertPhaseDivider } from '../RequestProfileTabActivitiesInsertPhaseDivider'
import { RequestProfileTabActivitiesPhase } from '../RequestProfileTabActivitiesPhase'
import { RequestProfileTabActivitiesSortablePhase } from '../RequestProfileTabActivitiesSortablePhase'

import {
  useOpenPhaseIds,
  usePhaseListDragEnd,
} from './RequestProfileTabActivitiesPhaseList.hooks'

import * as styles from './RequestProfileTabActivitiesPhaseList.module.css'

function RequestProfileTabActivitiesPhaseList({
  canAddPhase,
  canEditActivities,
  isPhasesLimitReached,
  permissions,
  phases,
  onAddPhase,
}) {
  const {
    handleReload,
    isReorderingActivities,
    setPhases,
    setReorderingActivities,
  } = useRequestProfileTabActivitiesContext()

  const currentPhases = phases ?? EMPTY_LIST

  const phaseIds = currentPhases.map(phase => getPhaseDndId(phase.id))

  const { handleOpen, handleToggleOpen, openPhaseIds } = useOpenPhaseIds(phases)

  const { handleDragEnd, handleReloadPhase } = usePhaseListDragEnd({
    handleReload,
    isReorderingActivities,
    phases: currentPhases,
    setPhases,
    setReorderingActivities,
  })

  function renderOverlay(activeId, activeData) {
    const type = activeData?.type

    if (isActivitiesPhaseType(type)) {
      const { isOpen, phase } = activeData ?? EMPTY_OBJECT

      return phase ? (
        <RequestProfileTabActivitiesPhase
          isOpen={isOpen}
          permissions={EMPTY_OBJECT}
          phase={phase}
          onReloadPhase={EMPTY_FUNCTION}
          onToggleOpen={EMPTY_FUNCTION}
        />
      ) : null
    }

    if (isActivitiesActivityType(type)) {
      const item = activeData?.item

      return item ? (
        <RequestProfileTabActivitiesActivityDragOverlay item={item} />
      ) : null
    }

    return null
  }

  const showInsertDividers = canAddPhase && !isPhasesLimitReached

  const listClassName = classNames(
    styles.RequestProfileTabActivitiesPhaseList,
    {
      [styles.insertable]: showInsertDividers,
    }
  )

  return currentPhases.length === 0 ? null : (
    <DragDropProvider
      className={listClassName}
      renderOverlay={renderOverlay}
      onDragEnd={handleDragEnd}
    >
      <SortableContextContainerProvider items={phaseIds}>
        {currentPhases.map((phase, index) => (
          <Fragment key={phase.id}>
            {showInsertDividers &&
            hasRequestProfileTabActivitiesPhaseAbove(index) ? (
              <RequestProfileTabActivitiesInsertPhaseDivider
                position={getRequestProfileTabActivitiesInsertPhasePosition(
                  index
                )}
                onAddPhase={onAddPhase}
              />
            ) : null}
            <RequestProfileTabActivitiesSortablePhase
              canEditActivities={canEditActivities}
              index={index}
              isOpen={openPhaseIds.has(phase.id)}
              permissions={permissions}
              phase={phase}
              onOpen={handleOpen}
              onReloadPhase={handleReloadPhase}
              onToggleOpen={handleToggleOpen}
            />
          </Fragment>
        ))}
      </SortableContextContainerProvider>
    </DragDropProvider>
  )
}

export default RequestProfileTabActivitiesPhaseList
